import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FormatSizeIcon from '@mui/icons-material/FormatSize';

// Предустановленные стили шрифтов
const fontStyles = {
  heading1: {
    name: 'Заголовок 1',
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    marginBottom: '1rem',
    color: '#1976d2',
  },
  heading2: {
    name: 'Заголовок 2',
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.3,
    marginBottom: '0.8rem',
    color: '#1976d2',
  },
  heading3: {
    name: 'Заголовок 3',
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    marginBottom: '0.6rem',
    color: '#1976d2',
  },
  subtitle: {
    name: 'Подзаголовок',
    fontSize: '1.25rem',
    fontWeight: 500,
    lineHeight: 1.5,
    marginBottom: '0.5rem',
    color: '#666',
    fontStyle: 'italic',
  },
  body: {
    name: 'Основной текст',
    fontSize: '1.1rem',
    fontWeight: 400,
    lineHeight: 1.7,
    marginBottom: '1rem',
    color: '#333',
  },
  quote: {
    name: 'Цитата',
    fontSize: '1.2rem',
    fontWeight: 400,
    lineHeight: 1.6,
    fontStyle: 'italic',
    color: '#555',
    borderLeft: '4px solid #1976d2',
    paddingLeft: '1rem',
    marginBottom: '1rem',
  },
  caption: {
    name: 'Подпись',
    fontSize: '0.9rem',
    fontWeight: 400,
    lineHeight: 1.4,
    color: '#777',
    fontStyle: 'italic',
  },
  code: {
    name: 'Код',
    fontSize: '0.95rem',
    fontWeight: 400,
    lineHeight: 1.5,
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    backgroundColor: '#f5f5f5',
    padding: '0.2rem 0.4rem',
    borderRadius: '4px',
    color: '#d73a49',
  },
};

// Цветовые схемы
const colorSchemes = {
  default: { name: 'По умолчанию', color: '#333' },
  primary: { name: 'Основной', color: '#1976d2' },
  secondary: { name: 'Вторичный', color: '#dc004e' },
  success: { name: 'Успех', color: '#2e7d32' },
  warning: { name: 'Предупреждение', color: '#ed6c02' },
  error: { name: 'Ошибка', color: '#d32f2f' },
  info: { name: 'Информация', color: '#0288d1' },
  dark: { name: 'Темный', color: '#424242' },
  light: { name: 'Светлый', color: '#757575' },
};

// Стилизованная карточка для предпросмотра стиля
const StyledStyleCard = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const FontStylePicker = ({ open, onClose, onStyleSelect }) => {
  const [selectedStyle, setSelectedStyle] = useState('body');
  const [selectedColor, setSelectedColor] = useState('default');
  const [fontSize, setFontSize] = useState(16);
  const [customText, setCustomText] = useState('Пример текста');

  const handleStyleSelect = (styleKey) => {
    setSelectedStyle(styleKey);
    const style = fontStyles[styleKey];
    setFontSize(parseFloat(style.fontSize) * 16); // Конвертируем rem в px
  };

  const handleApplyStyle = () => {
    const baseStyle = fontStyles[selectedStyle];
    const color = colorSchemes[selectedColor];
    
    // Создаем CSS стили
    const styles = {
      fontSize: `${fontSize}px`,
      fontWeight: baseStyle.fontWeight,
      lineHeight: baseStyle.lineHeight,
      color: color.color,
      fontFamily: baseStyle.fontFamily || 'inherit',
      fontStyle: baseStyle.fontStyle || 'normal',
      marginBottom: baseStyle.marginBottom || '1rem',
    };

    // Создаем HTML с примененными стилями
    const styledHtml = `<span style="${Object.entries(styles)
      .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
      .join('; ')}">${customText}</span>`;

    onStyleSelect(styledHtml);
    handleClose();
  };

  const handleClose = () => {
    setSelectedStyle('body');
    setSelectedColor('default');
    setFontSize(16);
    setCustomText('Пример текста');
    onClose();
  };

  const currentStyle = fontStyles[selectedStyle];
  const currentColor = colorSchemes[selectedColor];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormatSizeIcon />
          Настройка стиля текста
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Выберите базовый стиль:
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(fontStyles).map(([key, style]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <StyledStyleCard
                  selected={selectedStyle === key}
                  onClick={() => handleStyleSelect(key)}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography
                      sx={{
                        fontSize: style.fontSize,
                        fontWeight: style.fontWeight,
                        lineHeight: style.lineHeight,
                        color: style.color,
                        fontFamily: style.fontFamily || 'inherit',
                        fontStyle: style.fontStyle || 'normal',
                        mb: 0,
                      }}
                    >
                      {style.name}
                    </Typography>
                  </CardContent>
                </StyledStyleCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Цвет текста</InputLabel>
              <Select
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                label="Цвет текста"
              >
                {Object.entries(colorSchemes).map(([key, scheme]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          backgroundColor: scheme.color,
                          borderRadius: '50%',
                        }}
                      />
                      {scheme.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography gutterBottom>
              Размер шрифта: {fontSize}px
            </Typography>
            <Slider
              value={fontSize}
              onChange={(e, newValue) => setFontSize(newValue)}
              min={10}
              max={48}
              step={1}
              marks={[
                { value: 12, label: '12px' },
                { value: 16, label: '16px' },
                { value: 24, label: '24px' },
                { value: 36, label: '36px' },
              ]}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Предпросмотр */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Предпросмотр:
          </Typography>
          <Box
            sx={{
              p: 2,
              border: '1px solid #ddd',
              borderRadius: 1,
              backgroundColor: '#fafafa',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: `${fontSize}px`,
                fontWeight: currentStyle.fontWeight,
                lineHeight: currentStyle.lineHeight,
                color: currentColor.color,
                fontFamily: currentStyle.fontFamily || 'inherit',
                fontStyle: currentStyle.fontStyle || 'normal',
                mb: 0,
              }}
            >
              {customText}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          Отмена
        </Button>
        <Button 
          onClick={handleApplyStyle} 
          variant="contained"
        >
          Применить стиль
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FontStylePicker;
